/**
 * Clips asked for through the ompeer.com API. The control plane keeps no video of a broadcast:
 * it hands each request to the studio with a heartbeat answer (`clipRequests`), and the studio
 * cuts the clip from what it published and uploads it under the request's id.
 *
 * The clip is cut from a window of the fMP4 segments the publisher already produced (before they
 * are encrypted for the swarm): the init segment and the media segments that cover the length
 * asked for up to the moment it was asked, assembled exactly as the viewer's player assembles its
 * own clips.
 */
import { assembleClip, selectClipSegments } from '@ohmyprime/segmenter/clip-assembly';

/**
 * How much of the broadcast is kept to cut from: the longest clip that can be asked for (60 s), the
 * longest a request takes to arrive (it comes with a heartbeat, 30 s apart), and a little more.
 */
export const CLIP_WINDOW_MS = 95_000;
/** The window never holds more than this, whatever the bitrate: the oldest segments go first. */
export const CLIP_WINDOW_MAX_BYTES = 128 * 1024 * 1024;
/** Heartbeat period while a clip that was asked for has not been delivered yet. */
export const CLIP_PENDING_HEARTBEAT_MS = 5_000;
/** How long the cut waits for the segment that holds the moment the clip was asked for. */
export const CLIP_SEGMENT_WAIT_MS = 3_000;
/** The control plane refuses a clip longer than this. */
const MAX_CLIP_MS = 90_000;
/**
 * The control plane refuses a clip file over 64 MB. At the bitrates the studio can publish (up to 50 Mb/s)
 * a minute of broadcast is several times that, so a clip that would not fit is shortened from its start
 * until it does: it still ends at the moment it was asked for, and its real length is what is reported.
 */
export const CLIP_MAX_BYTES = 60 * 1024 * 1024;
/** A build may lower it (`VITE_OMP_CLIP_MAX_BYTES`): the end-to-end suite does, to see a clip shortened without publishing at 10 Mb/s. */
const BUILD_MAX_BYTES = Number((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_OMP_CLIP_MAX_BYTES);
const DEFAULT_MAX_BYTES = BUILD_MAX_BYTES > 0 && BUILD_MAX_BYTES < CLIP_MAX_BYTES ? BUILD_MAX_BYTES : CLIP_MAX_BYTES;

/** One clip the control plane asks for (`clipRequests` of a heartbeat answer). */
export interface ClipRequestSignal {
  id: string;
  title: string;
  durationSeconds: number;
  /** ISO 8601: the moment the clip should end at. */
  requestedAt: string;
  /** ISO 8601: an upload after this moment is refused. */
  expiresAt: string;
}

/** A media segment as the segmenter emits it. */
export interface WindowSegment {
  bytes: Uint8Array;
  durationMs: number;
  /** Wall-clock ms at which the segment's first frame was read. */
  capturedAt: number;
  keyframe?: boolean;
}

/** The `clipRequests` of a heartbeat answer; entries that are not requests are left out. */
export function parseClipRequests(body: unknown): ClipRequestSignal[] {
  const list = typeof body === 'object' && body !== null ? (body as { clipRequests?: unknown }).clipRequests : undefined;
  if (!Array.isArray(list)) return [];
  return list.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const e = entry as Record<string, unknown>;
    const ok =
      typeof e.id === 'string' &&
      e.id !== '' &&
      typeof e.durationSeconds === 'number' &&
      e.durationSeconds > 0 &&
      typeof e.requestedAt === 'string' &&
      Number.isFinite(Date.parse(e.requestedAt)) &&
      typeof e.expiresAt === 'string' &&
      Number.isFinite(Date.parse(e.expiresAt));
    return ok ? [{ id: e.id as string, title: typeof e.title === 'string' ? e.title : '', durationSeconds: e.durationSeconds as number, requestedAt: e.requestedAt as string, expiresAt: e.expiresAt as string }] : [];
  });
}

/** The recent segments of one broadcast, bounded in time and in bytes. */
export interface ClipWindow {
  push(segment: WindowSegment): void;
  /**
   * The segments for a clip of `durationMs` ending at `endAt`: those that began before `endAt`,
   * newest last, together at most `durationMs` long and, when `maxBytes` is given, at most that
   * large (the oldest are left out first; there is always one). Empty when nothing that old or
   * that recent is held.
   */
  select(endAt: number, durationMs: number, maxBytes?: number): WindowSegment[];
  /** Where the newest segment ends (wall-clock ms); 0 when empty. */
  end(): number;
  clear(): void;
  stats(): { segments: number; bytes: number; ms: number };
}

export function createClipWindow(maxMs: number = CLIP_WINDOW_MS, maxBytes: number = CLIP_WINDOW_MAX_BYTES): ClipWindow {
  let ring: WindowSegment[] = [];
  let bytes = 0;
  let ms = 0;
  return {
    push(segment) {
      ring.push(segment);
      bytes += segment.bytes.byteLength;
      ms += segment.durationMs;
      // The oldest go while the rest still cover the window, and while there is too much of it. The newest always stays.
      while (ring.length > 1 && (ms - ring[0]!.durationMs >= maxMs || bytes > maxBytes)) {
        const dropped = ring.shift()!;
        bytes -= dropped.bytes.byteLength;
        ms -= dropped.durationMs;
      }
    },
    select(endAt, durationMs, maxBytes = Infinity) {
      const upTo = ring.filter((segment) => segment.capturedAt < endAt);
      const chosen = selectClipSegments(upTo, durationMs);
      let size = chosen.reduce((total, segment) => total + segment.bytes.byteLength, 0);
      while (chosen.length > 1 && size > maxBytes) size -= chosen.shift()!.bytes.byteLength;
      while (chosen[0]?.keyframe === false) chosen.shift();
      return chosen;
    },
    end() {
      const last = ring.at(-1);
      return last ? last.capturedAt + last.durationMs : 0;
    },
    clear() {
      ring = [];
      bytes = 0;
      ms = 0;
    },
    stats: () => ({ segments: ring.length, bytes, ms }),
  };
}

export interface ClipFulfillerDeps {
  fetch: typeof fetch;
  /** `POST` target: the stream's clips route. */
  uploadUrl: string;
  /** `Authorization` with the stream key. */
  headers: Record<string, string>;
  /** The init segment that goes with the segments being published now; null before the first one. */
  init(): Uint8Array | null;
  /** Aborted when the broadcast ends: nothing is uploaded after that, and the window is let go. */
  signal: AbortSignal;
  log(level: 'debug' | 'info' | 'warn', message: string): void;
  now?: () => number;
  /** Resolves after `ms`, or sooner when a new segment arrives (the default waits on the clock alone). */
  wait?: (ms: number) => Promise<void>;
  windowMs?: number;
  windowMaxBytes?: number;
  /** The largest file the control plane takes (tests lower it). */
  maxClipBytes?: number;
}

/** What one broadcast does about clips: it keeps the window and answers the requests. */
export interface ClipFulfiller {
  /** A segment the publisher emitted, in clear. */
  onSegment(segment: WindowSegment): void;
  /**
   * What a heartbeat answered. New requests are cut and uploaded (not awaited here). Returns the
   * period the next heartbeat should come after while any request is still owed, else undefined.
   */
  onHeartbeat(body: unknown): number | undefined;
  /** Resolves when every upload in flight has settled (tests). */
  idle(): Promise<void>;
  window: ClipWindow;
}

type Outcome = 'done' | 'retry';

export function createClipFulfiller(deps: ClipFulfillerDeps): ClipFulfiller {
  const now = deps.now ?? (() => Date.now());
  const window = createClipWindow(deps.windowMs, deps.windowMaxBytes);
  /** Requests that need nothing more from this studio (delivered, refused for good, or run out), by id, with when they may be forgotten. */
  const settled = new Map<string, number>();
  /** Requests being cut or uploaded right now. */
  const working = new Map<string, Promise<void>>();
  let lastInit: Uint8Array | null = null;
  let wake: (() => void) | null = null;
  const wait =
    deps.wait ??
    ((ms: number) =>
      new Promise<void>((resolve) => {
        const timer = setTimeout(done, ms);
        function done(): void {
          clearTimeout(timer);
          wake = null;
          resolve();
        }
        wake = done;
      }));

  deps.signal.addEventListener('abort', () => {
    window.clear();
    wake?.();
  });

  async function upload(request: ClipRequestSignal): Promise<Outcome> {
    const endAt = Date.parse(request.requestedAt);
    const expiresAt = Date.parse(request.expiresAt);
    // The segment that holds the moment asked for is only whole a little later.
    const deadline = now() + CLIP_SEGMENT_WAIT_MS;
    while (window.end() < endAt && now() < deadline && now() < expiresAt && !deps.signal.aborted) {
      await wait(Math.min(250, Math.max(1, deadline - now())));
    }
    if (deps.signal.aborted) return 'done';
    if (now() >= expiresAt) {
      deps.log('warn', `clip ${request.id} was not delivered before its time ran out`);
      return 'done';
    }
    const init = deps.init();
    const wantedMs = Math.min(request.durationSeconds * 1000, MAX_CLIP_MS);
    const segments = init ? window.select(endAt, wantedMs, (deps.maxClipBytes ?? DEFAULT_MAX_BYTES) - init.byteLength) : [];
    if (!init || segments.length === 0) {
      // Nothing to cut from yet (the broadcast has just started): the next heartbeat tries again.
      deps.log('debug', `clip ${request.id}: nothing of that moment is held yet`);
      return 'retry';
    }
    const durationMs = Math.max(1, Math.round(segments.reduce((total, segment) => total + segment.durationMs, 0)));
    const file = assembleClip(init, segments.map((segment) => segment.bytes));
    let res: Response;
    try {
      res = await deps.fetch(deps.uploadUrl, {
        method: 'POST',
        headers: { ...deps.headers, 'Content-Type': 'video/mp4', 'X-Clip-Request-Id': request.id, 'X-Clip-Duration-Ms': String(durationMs) },
        body: new Blob([file], { type: 'video/mp4' }),
        credentials: 'omit',
        cache: 'no-store',
        signal: deps.signal,
      });
    } catch (err) {
      if (deps.signal.aborted) return 'done';
      deps.log('warn', `clip ${request.id}: upload failed (${err instanceof Error ? err.message : String(err)}); it is tried again`);
      return 'retry';
    }
    if (res.ok) {
      deps.log('info', `clip ${request.id} delivered (${Math.round(durationMs / 100) / 10} s of ${request.durationSeconds} s asked for, ${file.byteLength} bytes)`);
      return 'done';
    }
    // Only what another try can change is tried again: the server being busy or briefly unreachable. A request that is
    // gone or closed (404, 409), a key that is not this stream's (401, 403) and a file the server will not take (400,
    // 413, 415) would be answered the same way again.
    if (res.status === 408 || res.status === 429 || res.status >= 500) {
      deps.log('warn', `clip ${request.id}: upload answered ${res.status}; it is tried again`);
      return 'retry';
    }
    deps.log('warn', `clip ${request.id}: upload refused (${res.status}); not tried again`);
    return 'done';
  }

  return {
    window,
    onSegment(segment) {
      if (deps.signal.aborted) return;
      // A new init segment starts a new timeline: what was cut with the old one cannot be joined to it.
      const init = deps.init();
      if (init !== lastInit) {
        if (lastInit !== null) window.clear();
        lastInit = init;
      }
      window.push(segment);
      wake?.();
    },
    onHeartbeat(body) {
      const at = now();
      for (const [id, forgetAt] of settled) if (forgetAt <= at) settled.delete(id);
      let owed = 0;
      for (const request of parseClipRequests(body)) {
        if (settled.has(request.id)) continue;
        owed++;
        if (working.has(request.id) || deps.signal.aborted) continue;
        const work = upload(request)
          .then((outcome) => {
            // Remembered until the control plane itself stops listing it, and a while longer.
            if (outcome === 'done') settled.set(request.id, Math.max(now(), Date.parse(request.expiresAt)) + CLIP_WINDOW_MS);
          })
          .catch((err: unknown) => deps.log('warn', `clip ${request.id}: ${err instanceof Error ? err.message : String(err)}`))
          .finally(() => working.delete(request.id));
        working.set(request.id, work);
      }
      return owed > 0 ? CLIP_PENDING_HEARTBEAT_MS : undefined;
    },
    async idle() {
      while (working.size > 0) await Promise.all(working.values());
    },
  };
}

// One fulfiller per broadcast, made when it is first needed and let go with the object that stands for the broadcast.
const fulfillers = new WeakMap<object, ClipFulfiller>();

/** The fulfiller of `broadcast` (any object that lives as long as it does), made with `deps` the first time. */
export function clipFulfillerOf(broadcast: object, deps: () => ClipFulfillerDeps): ClipFulfiller {
  let fulfiller = fulfillers.get(broadcast);
  if (!fulfiller) {
    fulfiller = createClipFulfiller(deps());
    fulfillers.set(broadcast, fulfiller);
  }
  return fulfiller;
}
